import React, { useState, useRef } from "react";
import { useAuth } from "../lib/auth";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const photoInputRef = useRef(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    username: user?.username || "",
    bio: user?.bio || "",
    profile_photo: user?.profile_photo || null,
  });
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      // Immediately persist the new photo — takes effect app-wide
      await api.put("/users/me", { profile_photo: res.data.path });
      setForm((f) => ({ ...f, profile_photo: res.data.path }));
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/users/me", form);
      toast.success("Profile updated");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw.new_password || pw.new_password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated");
      setPw({ old_password: "", new_password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-1">Account</div>
        <h1 className="font-serif text-4xl">Settings</h1>
      </div>

      <section className="space-y-5 mb-12">
        <h2 className="font-serif text-2xl">Profile</h2>

        {/* Profile photo uploader */}
        <div className="flex items-center gap-5" data-testid="settings-photo-block">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-[hsl(var(--primary-soft))] border-2 border-border flex items-center justify-center overflow-hidden">
              {form.profile_photo ? (
                <img
                  src={fileUrl(form.profile_photo)}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  data-testid="settings-photo-preview"
                />
              ) : (
                <span className="text-3xl font-bold text-[hsl(var(--primary))]">
                  {user?.username?.[0]?.toUpperCase() || "?"}
                </span>
              )}
            </div>
            {uploadingPhoto && (
              <div className="absolute inset-0 rounded-full bg-background/70 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" />
              </div>
            )}
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="settings-photo-upload-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              {form.profile_photo ? "Change photo" : "Upload photo"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">JPG or PNG · Max 5 MB · Square works best.</p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoPick}
              data-testid="settings-photo-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Full name</Label>
          <Input className="rounded-xl mt-2 bg-background border-border h-11" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="settings-name" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Username</Label>
          <Input className="rounded-xl mt-2 bg-background border-border h-11" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} data-testid="settings-username" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Bio</Label>
          <Textarea className="rounded-xl mt-2 bg-background border-border" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="settings-bio" />
        </div>
        <Button onClick={save} disabled={saving} className="rounded-xl text-xs font-semibold tracking-wider bg-[hsl(var(--primary))] text-white" data-testid="settings-save">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </section>

      <section className="space-y-5 border-t border-border pt-8">
        <h2 className="font-serif text-2xl">Change password</h2>
        <div>
          <Label className="text-xs font-semibold text-muted-foreground tracking-wider">Current password (leave blank if not set)</Label>
          <Input type="password" className="rounded-xl mt-2 bg-background border-border h-11" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="settings-old-pw" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-muted-foreground tracking-wider">New password</Label>
          <Input type="password" className="rounded-xl mt-2 bg-background border-border h-11" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-pw" />
        </div>
        <Button variant="outline" onClick={changePw} className="rounded-xl text-xs font-semibold tracking-wider" data-testid="settings-change-pw">
          Update password
        </Button>
      </section>
    </div>
  );
}
